// pages/ModulesPage.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Eye, Edit, Trash2, Power, PowerOff } from "lucide-react";
import ModulesList from "../../../components/features/modules/ModulesList";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import PageListLayout from "../../../layouts/PageListLayout";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { Module } from "../../../types/features/Module";
import useModules from "../../../hooks/features/modules/useModules";
import { confirmModal } from "../../../helpers/confirmModal";




export default function ModulesPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(false);
   const {modules , deleteModule , toggleStatus } = useModules()
  const filteredModules = modules.filter(m =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (m.description && m.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleDelete = async (id: string) => {
    const confirmed = await confirmModal("Êtes-vous sûr de vouloir supprimer ce module ?")
    if (confirmed) {
       deleteModule(id)
      alertSuccess("Module supprimé avec succès !");
    }
  };

  const handleToggleStatus = async (id: string) => {
    const updatedModule = await toggleStatus(id)
    alertSuccess(`Module ${updatedModule?.is_active ? 'désactivé' : 'activé'} avec succès !`);
  };

  const getActionsMenu = (module: Module): Menu[] => {
    return [
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => {
          navigate("/admin/modules/details", { state: { module } });
          setSelectedModule(null);
        },
      },
      {
        label: "Modifier",
        icon: Edit,
        onClick: () => {
          navigate("/admin/modules/update", { state: { module } });
          setSelectedModule(null);
        },
      },
      {
        label: module.is_active ? "Désactiver" : "Activer",
        icon: module.is_active ? PowerOff : Power,
        onClick: () => {
          handleToggleStatus(module.id);
          setSelectedModule(null);
        },
        className: module.is_active ? "text-amber-600 hover:bg-amber-50" : "text-green-600 hover:bg-green-50",
      },
      {
        label: "Supprimer",
        icon: Trash2,
        onClick: () => {
          handleDelete(module.id);
          setSelectedModule(null);
        },
        className: "text-red-600 hover:bg-red-50",
      },
    ];
  };

  return (
    <PageListLayout title="">
      <div className="px-6 py-5 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-[#031B4E] tracking-tight">Modules</h1>
              <p className="text-sm text-slate-400 font-light mt-0.5">
                Gérez les fonctionnalités disponibles sur la plateforme
              </p>
            </div>
            
            <button
              onClick={() => navigate("/admin/modules/new")}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Plus size={18} />
              <span>Nouveau module</span>
            </button>
          </div>

          {/* Recherche */}
          <div className="mt-6">
            <div className="relative max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher un module..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Liste des modules */}
      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-[#031B4E] border-t-transparent"></div>
          </div>
        ) : (
          <ModulesList
            modules={filteredModules}
            onAction={(module) => setSelectedModule(module)}
            selectable={false}
          />
        )}
      </div>

      {/* Menu modal */}
      {selectedModule && (
        <MenuModal
          title={selectedModule.name}
          isOpen={true}
          onClose={() => setSelectedModule(null)}
          icon={null}
          menu={getActionsMenu(selectedModule)}
        />
      )}
    </PageListLayout>
  );
}